import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, } from 'react';
import { CSS_VAR_MAP } from './BKJThemeProvider.cssVars.js';
const STORAGE_KEY = 'bkj-color-scheme';
const DEFAULT_SCHEME = 'default';
// --- CSS variable override utilities ---
export const applyColorSchemeOverrides = (overrides) => {
    const applied = new Set();
    const colorVarMap = CSS_VAR_MAP.colors;
    for (const [tokenName, value] of Object.entries(overrides)) {
        const varName = colorVarMap[tokenName];
        if (varName && value) {
            document.documentElement.style.setProperty(varName, value);
            applied.add(varName);
        }
    }
    return applied;
};
export const removeColorSchemeOverrides = (appliedVarNames) => {
    for (const varName of appliedVarNames) {
        document.documentElement.style.removeProperty(varName);
    }
};
// --- React context and provider ---
const ColorSchemeContext = createContext({
    colorScheme: DEFAULT_SCHEME,
    setColorScheme: () => { },
});
export const ColorSchemeProvider = ({ colorScheme: initialColorScheme, colorSchemes = {}, children, }) => {
    const appliedVarNamesRef = useRef(new Set());
    const [colorScheme, setColorSchemeState] = useState(() => {
        // Precedence: prop > localStorage > 'default'
        if (initialColorScheme)
            return initialColorScheme;
        try {
            const persisted = localStorage.getItem(STORAGE_KEY);
            if (persisted && persisted !== DEFAULT_SCHEME) {
                // Validate the persisted name exists in the registry
                if (colorSchemes[persisted])
                    return persisted;
            }
        }
        catch {
            // localStorage unavailable — continue with default
        }
        return DEFAULT_SCHEME;
    });
    const setColorScheme = useCallback((name) => {
        // Remove previous overrides
        removeColorSchemeOverrides(appliedVarNamesRef.current);
        appliedVarNamesRef.current = new Set();
        if (name !== DEFAULT_SCHEME) {
            const overrides = colorSchemes[name];
            if (overrides) {
                appliedVarNamesRef.current = applyColorSchemeOverrides(overrides);
            }
            else {
                console.warn(`BKJThemeProvider: color scheme "${name}" is not registered, falling back to default`);
                name = DEFAULT_SCHEME;
            }
        }
        setColorSchemeState(name);
        try {
            localStorage.setItem(STORAGE_KEY, name);
        }
        catch {
            // localStorage unavailable — skip persistence
        }
    }, [colorSchemes]);
    // Apply initial scheme on mount (useLayoutEffect prevents flash of default colors)
    useLayoutEffect(() => {
        if (colorScheme !== DEFAULT_SCHEME) {
            const overrides = colorSchemes[colorScheme];
            if (overrides) {
                appliedVarNamesRef.current = applyColorSchemeOverrides(overrides);
            }
        }
        return () => {
            // Cleanup on unmount
            removeColorSchemeOverrides(appliedVarNamesRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (_jsx(ColorSchemeContext.Provider, { value: { colorScheme, setColorScheme }, children: children }));
};
// --- Hook ---
export const useColorScheme = () => useContext(ColorSchemeContext);
//# sourceMappingURL=BKJThemeProvider.colorScheme.js.map